import type { QuotationScheduleDayMember, QuotationScheduleDaySummary } from './quotationScheduleCalendarUtils';

/** Jumlah minimal petugas agar tim dianggap sudah terdefinisi (sama dengan aturan simpan tim). */
export const DEFINED_SURVEY_TEAM_MIN_MEMBERS = 2;

/** Nama petugas untuk kunci tim: trim, spasi dirapikan, tanpa beda huruf besar/kecil. */
export function normalizeSurveyOfficerName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLocaleLowerCase('id');
}

/** `name` boleh kosong: kunci roster jatuh ke `userId`. */
type SurveyRosterMember = Pick<QuotationScheduleDayMember, 'userId'> & { name?: string };

/**
 * Kunci roster di kalender: kombinasi nama petugas sampling.
 * Urutan nama tidak memengaruhi tim. Jika nama kosong, jatuh ke userId.
 */
export function surveyTeamMemberRosterKey(
  members: ReadonlyArray<SurveyRosterMember>,
): string | null {
  if (members.length === 0) return null;
  const names = members
    .map((member) => normalizeSurveyOfficerName(member.name ?? ''))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, 'id'));
  if (names.length > 0) return names.join('|');
  const ids = members
    .map((member) => member.userId.trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, 'id'));
  return ids.length > 0 ? ids.join('|') : null;
}

function yearMonthFromIsoDate(date: string): string {
  return date.slice(0, 7);
}

/**
 * Assign Tim 1..N ke komposisi petugas unik dalam bulan kalender yang sama.
 * Counter kembali ke Tim 1 pada bulan berikutnya (reset tiap YYYY-MM).
 *
 * Map key: `${YYYY-MM}|${rosterKey}` → nomor tim.
 */
export function buildMonthlySurveyTeamNumbersByRoster(
  summariesByDate: Map<string, QuotationScheduleDaySummary[]>,
): Map<string, number> {
  const result = new Map<string, number>();
  const dates = [...summariesByDate.keys()].sort((a, b) => a.localeCompare(b, 'id'));
  const nextIndexByMonth = new Map<string, number>();

  for (const date of dates) {
    const yearMonth = yearMonthFromIsoDate(date);
    if (!/^\d{4}-\d{2}$/.test(yearMonth)) continue;

    const summaries = [...(summariesByDate.get(date) ?? [])].sort((a, b) =>
      a.quotationNumber.localeCompare(b.quotationNumber, 'id'),
    );

    for (const summary of summaries) {
      if (summary.members.length < DEFINED_SURVEY_TEAM_MIN_MEMBERS) continue;
      const rosterKey = surveyTeamMemberRosterKey(summary.members);
      if (!rosterKey) continue;

      const lookupKey = `${yearMonth}|${rosterKey}`;
      if (result.has(lookupKey)) continue;

      const next = (nextIndexByMonth.get(yearMonth) ?? 0) + 1;
      nextIndexByMonth.set(yearMonth, next);
      result.set(lookupKey, next);
    }
  }

  return result;
}

export function resolveMonthlySurveyTeamNumber(
  date: string,
  members: ReadonlyArray<SurveyRosterMember>,
  teamNumberByLookupKey: Map<string, number>,
): number | null {
  if (members.length < DEFINED_SURVEY_TEAM_MIN_MEMBERS) return null;
  const rosterKey = surveyTeamMemberRosterKey(members);
  if (!rosterKey) return null;
  const yearMonth = yearMonthFromIsoDate(date);
  return teamNumberByLookupKey.get(`${yearMonth}|${rosterKey}`) ?? null;
}

/** Label petugas di kartu: nama tim bebas, atau `Tim N - A, B`, atau daftar nama saja. */
export function formatSurveyMemberNames(
  members: ReadonlyArray<Pick<QuotationScheduleDayMember, 'name' | 'userId'>>,
  teamNumber?: number | null,
  teamName?: string | null,
): string {
  if (members.length === 0) return '—';
  const names = members.map((member) => member.name).join(', ');
  const customName = String(teamName ?? '').trim();
  if (customName) {
    return `${customName} - ${names}`;
  }
  if (teamNumber != null && members.length >= DEFINED_SURVEY_TEAM_MIN_MEMBERS) {
    return `Tim ${teamNumber} - ${names}`;
  }
  return names;
}
